import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Fleet } from '../types';
import { Vehicle } from '../types/vehicle';
import { v4 as uuidv4 } from 'uuid';

const FLEETS_COLLECTION = 'fleets';

export const fleetService = {
  // Méthodes existantes
  async getFleets(organizationId: string): Promise<Fleet[]> {
    try {
      const q = query(
        collection(db, FLEETS_COLLECTION),
        where('organizationId', '==', organizationId),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Fleet));
    } catch (error) {
      console.error('Error getting fleets:', error);
      throw error;
    }
  },

  async getFleet(id: string): Promise<Fleet | null> {
    try {
      const docRef = doc(db, FLEETS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Fleet;
      }
      return null;
    } catch (error) {
      console.error('Error getting fleet:', error);
      throw error;
    }
  },

  async createFleet(
    organizationId: string,
    data: { name: string; description?: string },
    createdBy: string
  ): Promise<string> {
    try {
      // Créer un objet sans les champs undefined
      const fleetData: Record<string, any> = {
        name: data.name,
        organizationId,
        createdBy,
        updatedBy: createdBy,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Ajouter description seulement si elle est définie et non vide
      if (data.description !== undefined && data.description !== '') {
        fleetData.description = data.description;
      }

      const docRef = await addDoc(collection(db, FLEETS_COLLECTION), fleetData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating fleet:', error);
      throw error;
    }
  },

  async updateFleet(
    id: string,
    data: { name?: string; description?: string },
    updatedBy: string
  ): Promise<void> {
    try {
      const docRef = doc(db, FLEETS_COLLECTION, id);
      
      // Créer un objet sans les champs undefined
      const updateData: Record<string, any> = {
        updatedBy,
        updatedAt: Timestamp.now(),
      };
      
      // Ajouter les champs seulement s'ils sont définis
      if (data.name !== undefined) {
        updateData.name = data.name;
      }
      
      if (data.description !== undefined) {
        // Si description est une chaîne vide, la stocker comme chaîne vide
        // sinon Firestore pourrait la rejeter comme undefined
        updateData.description = data.description === '' ? '' : data.description;
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating fleet:', error);
      throw error;
    }
  },

  async deleteFleet(id: string): Promise<void> {
    try {
      const docRef = doc(db, FLEETS_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting fleet:', error);
      throw error;
    }
  },

  // Nouvelles méthodes pour la réservation
  async updateFleetReservationSettings(
    id: string,
    settings: {
      publicLinkEnabled?: boolean;
      publicLinkId?: string;
      allowedEmailDomains?: string[];
      requireEmailValidation?: boolean;
    },
    updatedBy: string
  ): Promise<void> {
    try {
      const docRef = doc(db, FLEETS_COLLECTION, id);
      
      // Créer un objet sans les champs undefined
      const updateData: Record<string, any> = {
        updatedBy,
        updatedAt: Timestamp.now(),
      };
      
      // Ajouter les champs seulement s'ils sont définis
      if (settings.publicLinkEnabled !== undefined) {
        updateData.publicLinkEnabled = settings.publicLinkEnabled;
      }
      
      if (settings.publicLinkId !== undefined) {
        updateData.publicLinkId = settings.publicLinkId;
      }
      
      if (settings.allowedEmailDomains !== undefined) {
        updateData.allowedEmailDomains = settings.allowedEmailDomains;
      }
      
      if (settings.requireEmailValidation !== undefined) {
        updateData.requireEmailValidation = settings.requireEmailValidation;
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating fleet reservation settings:', error);
      throw error;
    }
  },

  async generatePublicLink(id: string): Promise<string> {
    try {
      const docRef = doc(db, FLEETS_COLLECTION, id);
      const publicLinkId = uuidv4();
      
      await updateDoc(docRef, {
        publicLinkId,
        updatedAt: Timestamp.now(),
      });
      
      return publicLinkId;
    } catch (error) {
      console.error('Error generating public link:', error);
      throw error;
    }
  },

  async getFleetByPublicLink(publicLinkId: string): Promise<Fleet | null> {
    try {
      const q = query(
        collection(db, FLEETS_COLLECTION),
        where('publicLinkId', '==', publicLinkId),
        where('publicLinkEnabled', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() } as Fleet;
    } catch (error) {
      console.error('Error getting fleet by public link:', error);
      throw error;
    }
  },

  async getAvailableVehiclesInFleet(
    fleetId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Vehicle[]> {
    try {
      // Cette méthode sera implémentée plus tard lorsque nous aurons le reservationService
      // pour vérifier les disponibilités des véhicules
      
      // Pour l'instant, nous retournons simplement tous les véhicules de la flotte
      const q = query(
        collection(db, 'vehicles'),
        where('fleetId', '==', fleetId),
        where('status', '==', 'active')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vehicle));
    } catch (error) {
      console.error('Error getting available vehicles in fleet:', error);
      throw error;
    }
  },
};

export default fleetService;
